// Pure helpers for the Event page's year/month filter and search. Kept free of
// React so the filter and grouping rules are unit-testable on their own.
//
// Filtering happens in the browser: GET /events returns the whole list, and the
// other list pages (Upcoming, Service History) narrow it client-side too. If the
// list ever grows past a few hundred events this is where server-side query
// params would replace the date and query matching.

export const ALL = "all";

const MONTH_NAMES = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];

// Dates are read in local time, the same way the month headings render them, so
// a filter selection can never disagree with the group an event lands in.
const startOf = (event) => (event.start_date ? new Date(event.start_date) : null);

const inYear = (date, year) => year === ALL || String(date.getFullYear()) === String(year);

export const buildYearOptions = (events) => {
    const years = new Set();
    events.forEach((event) => {
        const date = startOf(event);
        if (date) years.add(date.getFullYear());
    });

    return [
        { value: ALL, label: "All years" },
        ...[...years]
            .sort((a, b) => b - a)
            .map((year) => ({ value: String(year), label: String(year) })),
    ];
};

// Only months that actually hold an event are offered, so the dropdown can
// never lead to an empty result. `year` scopes which months those are.
export const buildMonthOptions = (events, year = ALL) => {
    const months = new Set();
    events.forEach((event) => {
        const date = startOf(event);
        if (date && inYear(date, year)) months.add(date.getMonth());
    });

    return [
        { value: ALL, label: "All months" },
        ...[...months]
            .sort((a, b) => a - b)
            .map((month) => ({ value: String(month), label: MONTH_NAMES[month] })),
    ];
};

// Undated events survive only while both date dropdowns are open: they have no
// date to match, so showing them under a chosen month would be a lie.
export const filterEvents = (events, { year = ALL, month = ALL, search = "" } = {}) => {
    const query = search.trim().toLowerCase();
    const dateScoped = year !== ALL || month !== ALL;

    return events.filter((event) => {
        const date = startOf(event);
        if (dateScoped) {
            if (!date) return false;
            if (!inYear(date, year)) return false;
            if (month !== ALL && String(date.getMonth()) !== String(month)) return false;
        }

        if (!query) return true;
        return [event.title, event.description, event.location].some((field) =>
            (field || "").toLowerCase().includes(query)
        );
    });
};

// Returns [label, events] pairs, newest month first, with undated events in a
// trailing "Date TBD" group.
export const groupEventsByMonth = (events) => {
    const groups = new Map();
    const dated = events
        .filter((event) => event.start_date)
        .sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
    const undated = events.filter((event) => !event.start_date);

    dated.forEach((event) => {
        const date = new Date(event.start_date);
        const label = `${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
        if (!groups.has(label)) groups.set(label, []);
        groups.get(label).push(event);
    });
    if (undated.length > 0) groups.set("Date TBD", undated);

    return [...groups.entries()];
};
